import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchWorkloadWorkkardsRequest,
  fetchWorkloadActivitiesRequest,
  setWorkloadActiveTab,
} from "@container/AdminPanel/features/users/usersSlice";
import {
  selectWorkloadWorkkards,
  selectWorkloadActivities,
  selectWorkloadActiveTab,
  selectWorkloadLoading,
} from "@container/AdminPanel/features/users/usersSelectors";
import Icon from "../../../icons";
import DynamicTable from "../../Table";
import CheckBox from "../../Checkbox/index";

const ManageWorkload = ({ user, type, onBack }) => {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const effectiveUserId = user?.id || userId;
  const effectiveType = type || searchParams.get("type") || "workkards";

  const dispatch = useDispatch();

  const workkards = useSelector(selectWorkloadWorkkards);
  const activities = useSelector(selectWorkloadActivities);
  const activeTab = useSelector(selectWorkloadActiveTab);
  const loading = useSelector(selectWorkloadLoading);

  const [selectedRows, setSelectedRows] = useState({});
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    if (effectiveUserId && activeTab) {
      if (activeTab === "workkards" && workkards.length === 0) {
        dispatch(fetchWorkloadWorkkardsRequest({ userId: effectiveUserId }));
      } else if (activeTab === "activities" && activities.length === 0) {
        dispatch(fetchWorkloadActivitiesRequest({ userId: effectiveUserId }));
      }
    }
  }, [dispatch, effectiveUserId, activeTab]);

  useEffect(() => {
    if (effectiveType) {
      dispatch(setWorkloadActiveTab(effectiveType));
    }
  }, []);

  const handleTabChange = useCallback(
    (tab) => {
      navigate(`/users/manageworkload/${effectiveUserId}?type=${tab}`, {
        replace: true,
      });

      dispatch(setWorkloadActiveTab(tab));

      setSelectedRows({});
      setSelectAll(false);

      if (effectiveUserId && tab !== activeTab) {
        if (tab === "workkards") {
          dispatch(fetchWorkloadWorkkardsRequest({ userId: effectiveUserId }));
        } else if (tab === "activities") {
          dispatch(fetchWorkloadActivitiesRequest({ userId: effectiveUserId }));
        }
      }
    },
    [navigate, effectiveUserId, dispatch, activeTab],
  );

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack();
    } else {
      navigate("/users");
    }
  }, [onBack, navigate]);

  const hasSelectedRows = Object.values(selectedRows).some(
    (checked) => checked,
  );

  const currentData = activeTab === "workkards" ? workkards : activities;

  const handleSelectAll = useCallback(
    (checked) => {
      setSelectAll(checked);
      const newSelectedRows = {};
      if (checked) {
        currentData.forEach((row) => {
          newSelectedRows[row.id] = true;
        });
      }
      setSelectedRows(newSelectedRows);
    },
    [currentData],
  );

  useEffect(() => {
    const allSelected =
      currentData.length > 0 &&
      currentData.every((row) => selectedRows[row.id]);
    setSelectAll(allSelected);
  }, [selectedRows, currentData]);

  const handleCheckboxChange = useCallback((rowId, checked) => {
    setSelectedRows((prev) => ({ ...prev, [rowId]: checked }));
  }, []);

  const workkardColumns = useMemo(
    () => [
      {
        key: "workkardNumber",
        header: "Workkard No",
        headerRender: () => (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <CheckBox
              item={{
                id: "select-all-workkards",
                checked: selectAll,
                label: "",
              }}
              onChange={() => handleSelectAll(!selectAll)}
            />
            <span>Workkard No</span>
          </div>
        ),
        render: (row) => (
          <CheckBox
            item={{
              id: `workkard-${row.id}`,
              checked: !!selectedRows[row.id],
              label: row.workkardNumber,
            }}
            onChange={() => handleCheckboxChange(row.id, !selectedRows[row.id])}
          />
        ),
      },
      {
        key: "sponserName",
        header: "Workkard Sponsor",
        render: (row) => row.sponserName,
      },
      {
        key: "status",
        header: "Status",
        render: (row) => (
          <span className={`label-ntf label-${row.status}`}>{row.status}</span>
        ),
      },
    ],
    [selectAll, selectedRows, handleSelectAll, handleCheckboxChange],
  );

  const activityColumns = useMemo(
    () => [
      {
        key: "activityTitle",
        header: "Activity Title",
        headerRender: () => (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <CheckBox
              item={{
                id: "select-all-activities",
                checked: selectAll,
                label: "",
              }}
              onChange={() => handleSelectAll(!selectAll)}
            />
            <span>Activity Title</span>
          </div>
        ),
        render: (row) => (
          <CheckBox
            item={{
              id: `activity-${row.id}`,
              checked: !!selectedRows[row.id],
              label: row.activityTitle,
            }}
            onChange={() => handleCheckboxChange(row.id, !selectedRows[row.id])}
          />
        ),
      },
      {
        key: "workkardNumber",
        header: "Workkard No",
        render: (row) => row.workkardNumber,
      },
      {
        key: "localDueDate",
        header: "Due Date",
        render: (row) => row.localDueDate,
      },
      {
        key: "ownerName",
        header: "Activity Owner",
        render: (row) => row.ownerName,
      },
      {
        key: "assigneeName",
        header: "Activity Assignee",
        render: (row) => row.assigneeName,
      },
      {
        key: "resolution",
        header: "Resolution",
        render: (row) => (
          <span
            className={
              row.statusColorClass || `label-ntf label-${row.resolution}`
            }
          >
            {row.resolution}
          </span>
        ),
      },
    ],
    [selectAll, selectedRows, handleSelectAll, handleCheckboxChange],
  );

  return (
    <div className="org_access">
      <div className="alignMiddle rowFlex wth-12">
        <div className="wth-12">
          <h3>
            <a href="javascript:void(0)" onClick={handleBack}>
              Users
            </a>{" "}
            / Manage Workload
            {user && ` - ${user.displayName}`}
          </h3>
        </div>
      </div>

      <div className="alignMiddle">
        <div className="tabNav mt">
          <div className="tabs" data-test="tabs">
            <a
              href="javascript:void(0)"
              className={activeTab === "workkards" ? "active" : ""}
              data-test="tabls-workkards"
              onClick={() => handleTabChange("workkards")}
            >
              Workkards <span className="ml__4">({workkards.length})</span>
            </a>
            <a
              href="javascript:void(0)"
              className={activeTab === "activities" ? "active" : ""}
              data-test="tabls-activities"
              onClick={() => handleTabChange("activities")}
            >
              Activitys <span className="ml__4">({activities.length})</span>
            </a>
          </div>
        </div>
        <div className="alignMiddle" data-test="sort">
          <button
            className={!hasSelectedRows ? "cc-btn disabled" : "btn-cc__primary"}
            onClick={() => console.log("clicked")}
            disabled={!hasSelectedRows}
          >
            {activeTab === "workkards"
              ? "Transfer Workload"
              : "Change Activity Owner"}
          </button>
          {activeTab !== "workkards" && (
            <button
              className={
                !hasSelectedRows ? "cc-btn disabled" : "btn-cc__primary"
              }
              onClick={() => console.log("clicked")}
              disabled={!hasSelectedRows}
            >
              Change Activity Assignee
            </button>
          )}
        </div>
      </div>

      <DynamicTable
        columns={activeTab === "workkards" ? workkardColumns : activityColumns}
        data={activeTab === "workkards" ? workkards : activities}
      />
    </div>
  );
};

export default ManageWorkload;
